'use client';

import { useTranslations } from 'next-intl';
import { type ChangeEvent, type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { cn } from '@/app/utils';
import { TTS_PITCH, TTS_SPEED, TTS_VOLUME } from '../constant';

interface TTSSettingsProps {
  voiceId: string;
  speed: number;
  pitch: number;
  volume: number;
  onVoiceChange: (voiceId: string) => void;
  onSpeedChange: (value: number) => void;
  onPitchChange: (value: number) => void;
  onVolumeChange: (value: number) => void;
}

interface SliderFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  suffix?: string;
}

const getStepPrecision = (step: number) => {
  const [, decimals = ''] = String(step).split('.');
  return decimals.length;
};

const clampValue = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const SliderField: FC<SliderFieldProps> = ({ label, value, min, max, step, onChange, suffix }) => {
  const [inputValue, setInputValue] = useState(String(value));

  const precision = useMemo(() => getStepPrecision(step), [step]);

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const normalizeValue = useCallback(
    (nextValue: number) => {
      const clampedValue = clampValue(nextValue, min, max);
      return Number(clampedValue.toFixed(precision));
    },
    [max, min, precision],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(Number(e.target.value));
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value;

      setInputValue(nextValue);

      if (nextValue === '' || nextValue === '-' || nextValue === '.' || nextValue === '-.') {
        return;
      }

      const parsedValue = Number(nextValue);
      if (Number.isNaN(parsedValue)) {
        return;
      }

      onChange(normalizeValue(parsedValue));
    },
    [normalizeValue, onChange],
  );

  const handleInputBlur = useCallback(() => {
    if (inputValue === '' || inputValue === '-' || inputValue === '.' || inputValue === '-.') {
      setInputValue(String(value));
      return;
    }

    const parsedValue = Number(inputValue);
    if (Number.isNaN(parsedValue)) {
      setInputValue(String(value));
      return;
    }

    const normalizedValue = normalizeValue(parsedValue);
    setInputValue(String(normalizedValue));
    onChange(normalizedValue);
  }, [inputValue, normalizeValue, onChange, value]);

  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="flex min-h-5 items-center justify-between gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="w-[86px] shrink-0 text-sm text-system-white leading-[1.4]">{label}</span>
        <div className="min-w-0 flex-1">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={handleChange}
            className={cn(
              'h-1 w-full cursor-pointer appearance-none rounded-full bg-system-white08 outline-none',
              '[&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-system-white',
              '[&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:bg-system-white',
            )}
            style={{
              background: `linear-gradient(to right, rgba(255,255,255,0.64) 0%, rgba(255,255,255,0.64) ${percentage}%, rgba(255,255,255,0.08) ${percentage}%, rgba(255,255,255,0.08) 100%)`,
            }}
          />
        </div>
      </div>
      <div className="flex h-6 w-[76px] shrink-0 items-center justify-center gap-1 rounded-8 bg-system-white08 px-3 text-system-white/40 text-xs leading-[1.4]">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          className="w-full appearance-none border-none bg-transparent p-0 text-center text-system-white text-xs leading-[1.4] outline-none [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
        />
        {suffix ? <span className="shrink-0">{suffix}</span> : null}
      </div>
    </div>
  );
};

export const TTSSettings: FC<TTSSettingsProps> = ({
  voiceId,
  speed,
  pitch,
  volume,
  onVoiceChange,
  onSpeedChange,
  onPitchChange,
  onVolumeChange,
}) => {
  const t = useTranslations();
  const [isVoiceSelectOpen, setIsVoiceSelectOpen] = useState(false);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex gap-4 py-3 lg:flex-col">
        <div className="min-w-0 flex-1 self-stretch">
          <VoiceSelectionDialog
            open={isVoiceSelectOpen}
            onOpenChange={setIsVoiceSelectOpen}
            title={t('tools.textToSpeech.settings.voice')}
            value={voiceId}
            onConfirm={(nextVoiceId) => onVoiceChange(nextVoiceId)}
          />
        </div>

        <div className="flex flex-1 flex-col justify-between gap-2 self-stretch">
          <SliderField
            label={t('tools.textToSpeech.settings.speed')}
            value={speed}
            min={TTS_SPEED.MIN}
            max={TTS_SPEED.MAX}
            step={TTS_SPEED.STEP}
            onChange={onSpeedChange}
            suffix="x"
          />
          <SliderField
            label={t('tools.textToSpeech.settings.pitch')}
            value={pitch}
            min={TTS_PITCH.MIN}
            max={TTS_PITCH.MAX}
            step={TTS_PITCH.STEP}
            onChange={onPitchChange}
          />
          <SliderField
            label={t('tools.textToSpeech.settings.volume')}
            value={volume}
            min={TTS_VOLUME.MIN}
            max={TTS_VOLUME.MAX}
            step={TTS_VOLUME.STEP}
            onChange={onVolumeChange}
          />
        </div>
      </div>
    </div>
  );
};
